import React, {useContext, useState} from "react";
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _get from "lodash/get";

import {isDisabled, isRequired} from "../utils";
import FormContext from "../form-context";
import kebabCase from "lodash/kebabCase";
import FieldControlStatus, {FieldControlMessage} from './field-control-status';

import Field from "./field";
import CONTROLS from "./index";

InputGroup.propTypes = {
    className: PropTypes.string,
    groupClassName: PropTypes.string,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onFieldChange: PropTypes.func.isRequired,
    optional: PropTypes.bool,
};

InputGroup.defaultProps = {
    optional: true,
};

export default function InputGroup(props) {
    const context = useContext(FormContext);
    const [headerActionsTarget, setHeaderActionsTarget] = useState(null);
    const required = isRequired(props, context.values);
    const disabled = isDisabled(props, context.values);
    const isNotCheckbox = props.type !== 'checkbox' && props.type !== 'switch';
    const error = _get(context.errors, props.name, false);

    const fieldControl = context.fieldControl?.[props.name];
    const showControl = fieldControl?.manual || fieldControl?.pendingRevert;
    const hasLabel = !!props.label;
    const showEdit = props.editing && !props.system;
    const showOptional = !showControl && props.optional
        && !props.editing
        && !required
        && !props.readOnly
        && !props.disabled
        && props.type !== 'radio'
        && props.type !== 'checkbox-list'
        && props.type !== 'colour-picker'
        && props.type !== 'form-text'
        && context.layout !== 'product-search-block'
        && !props.type.startsWith("plugins");
    const id = props.id || props.name;
    const controlStatus = <FieldControlStatus name={props.name} label={props.label} id={id}
        disabled={disabled} readOnly={props.readOnly}/>;
    
    // Self-contained shared controls own their label and compact/default presentation.
    if (CONTROLS[props.type]?.wrapWithInputGroup === false) {
        return <Field {...props} id={id} error={error} required={props.editing ? false : required} disabled={disabled}/>;
    }

    return (
        <div className={classNames(props.groupClassName, `grow control-${props.type}-${kebabCase(props.label)}`)}>
            {isNotCheckbox && context.layout !== 'product-search-block' && (hasLabel || showEdit || showOptional || showControl)  ? (
                <div className={`flex flex-wrap items-start gap-2 mb-1 ${props.label ? 'justify-between' : 'justify-end'}`}>
                    {hasLabel ? (
                        <label htmlFor={id} className={classNames(
                            "block text-sm font-semibold",
                            error && typeof error !== 'object' && !props.disabled ? "text-error-500" : "text-gray-700"
                        )}>
                            {props.label}
                        </label>
                    ) : null}

                    <div className="flex flex-wrap items-center justify-end gap-2">
                        {showEdit ? (
                            <span
                                className={"text-xs font-medium text-primary-500 leading-5 cursor-pointer hover:underline"}
                                onClick={props.onEditFieldSettings}
                            >Edit</span>
                        ) : null}
                        {controlStatus}
                        {showOptional ? (
                            <span className={classNames(
                                "text-xs font-medium",
                                "leading-5",
                                error && typeof error !== 'object' && !props.disabled ? "text-error-500" : "text-gray-500"
                            )}>Optional</span>
                        ) : null}
                        {props.allowClearAll && hasLabel ? <div ref={setHeaderActionsTarget}/> : null}
                    </div>
                </div>
            ) : null}
            {!isNotCheckbox && showControl ? <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 grow"><Field {...props} id={id} error={error} required={props.editing ? false : required} disabled={disabled} /></div>
                {controlStatus}
            </div> : <Field {...props} headerActionsTarget={headerActionsTarget} id={id} error={error} required={props.editing ? false : required} disabled={disabled} />}
            <FieldControlMessage name={props.name} id={id}/>
        </div>
    )
}
